import React from "react";
import cx from "classnames";
import ContentLoader from "react-content-loader";
import stylesV2 from "./DoctorAvatar.module.scss";
import stylesV3 from "./DoctorAvatarV2.module.scss";
import doctorPlaceholder from "images/doctor-placeholder.svg";
import { Doctor } from "types/doctor";
import { Office } from "types/office";
import { doctorFullName } from "utils/doctorFullName";
import { useSchedulingABVariant } from "features/scheduling/utils/useSchedulingABVariant";

interface DoctorAvatarProps {
  doctor: Doctor | undefined;
  size?: "xsmall" | "small" | "normal";
  loaded?: boolean;
  className?: string;
  selectedOffice?: Office;
  callFrom?: string;
}

// A 1x1 pixel to serve as the <img> src. This lets us use an <img> tag combined with the background-image fallbacks to handle a missing headshot image gracefully.
const transparentPixel =
  "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=";
declare let originalVEHheadshot: string;

const cssUrl = (url: string) => `url("${url}")`;

export const DoctorAvatar: React.FC<DoctorAvatarProps> = ({
  doctor,
  size = "normal",
  loaded = true,
  className,
  selectedOffice,
  callFrom,
}) => {
  const { isV3 } = useSchedulingABVariant();
  const styles = isV3 ? stylesV3 : stylesV2;
  if (!loaded || !doctor) {
    return (
      <ContentLoader
        speed={2}
        viewBox="0 0 44 44"
        backgroundColor="var(--gray-02)"
        foregroundColor="var(--gray-04)"
        className={cx(styles.avatar, className, {
          [styles.small]: size === "small",
          [styles.xsmall]: size === "xsmall",
        })}
      >
        <circle cx="22" cy="22" r="22" />
      </ContentLoader>
    );
  }

  const headshotImage = doctor.headshotUrl ? `${cssUrl(doctor.headshotUrl)},` : "";
  let styleAvatar;

  if (
    (selectedOffice &&
      selectedOffice.namechangeVEH &&
      doctor.offersVEH &&
      doctor.headshotUrl &&
      doctor.firstName === "Open Network") ||
    (callFrom === "nearby" &&
      selectedOffice &&
      selectedOffice.namechangeVEH &&
      doctor.offersVEH &&
      doctor.headshotUrl &&
      doctor.firstName === "Open Network")
  ) {
    originalVEHheadshot =
      typeof originalVEHheadshot !== "undefined" ? originalVEHheadshot : "";

    if (callFrom === "nearby" && originalVEHheadshot) {
      styleAvatar = {
        backgroundImage: `${cssUrl(originalVEHheadshot)}, ${cssUrl(doctorPlaceholder)}`,
      };
    } else {
      styleAvatar = {
        backgroundImage: `${headshotImage} ${cssUrl(doctorPlaceholder)}`,
        borderRadius: "initial",
        backgroundColor: callFrom === "successPage" ? "var(--gray-05)" : "var(--white)",
        width: "78px",
        height: "43px",
        backgroundRepeat: "no-repeat",
        backgroundSize: "contain",
      };
    }
  } else {
    styleAvatar = {
      backgroundImage: `${headshotImage} ${cssUrl(doctorPlaceholder)}`,
    };
  }
  return (
    <img
      alt={doctorFullName(doctor, selectedOffice?.namechangeVEH)}
      src={transparentPixel}
      className={cx(styles.avatar, className, {
        [styles.small]: size === "small",
        [styles.xsmall]: size === "xsmall",
      })}
      style={styleAvatar}
    />
  );
};
